import ScrollTrigger from 'gsap/ScrollTrigger'
import gsap from 'gsap'

// General
import Events from 'assets/js/app/events'
import ScrollTarget from 'assets/js/app/ScrollTarget'
import LazyLoad from 'assets/js/app/LazyLoad'
import MetaColor from 'assets/js/app/MetaColor'
import MobileVideo from 'assets/js/app/MobileVideo'
import NavHeight from 'assets/js/app/NavHeight'
import RefreshScrollTrigger from 'assets/js/app/RefreshScrollTrigger'
import ParallaxCards from 'assets/js/app/ParallaxCards'
import PlayInView from 'assets/js/app/PlayInView'
import LowPowerMode from 'assets/js/app/LowPowerMode'
import IsMobileBreakpoint from 'assets/js/app/IsMobileBreakpoint'
import InvictusBgVideo from 'assets/js/app/InvictusBgVideo'
import GlobalTransitions from 'assets/js/app/GlobalTransitions'
import UHFSearch from 'assets/js/app/UHFSearch'
import FocalPoint from 'assets/js/app/FocalPoint'

// Template Parts
import A11yWidget from 'template-parts/a11y-widget/a11y-widget'
import AudioPlayer from 'template-parts/audio-player/audio'
import Breadcrumb from 'template-parts/breadcrumb/breadcrumb'
import ButtonToggle from 'template-parts/buttons/button-toggle'
import ButtonVideo from 'template-parts/buttons/button-video'
import Card from 'template-parts/cards/card'
import CardCrosslink from 'template-parts/card-crosslink/card-crosslink'
import CardInset from 'template-parts/cards/card-inset'
import CardLegacy from 'template-parts/cards/card-legacy'
import MediaImage from 'template-parts/media/image'
import ParallaxGrid from 'template-parts/parallax-grid/parallax-grid'
import SocialShare from 'template-parts/social-share/social-share'
import VideoPlayer from 'template-parts/video-player/video-player'
import Xray from 'template-parts/xray/xray'

// Blocks
import AdlamFullWidth from 'blocks/adlam_full_width/adlam_full_width'
import AdlamHomePage from 'blocks/adlam_home_page/adlam_home_page'
import AdlamPostcards from 'blocks/adlam_shareable_postcards/adlam_shareable_postcards'
import AllStories from 'blocks/all_stories/all_stories'
import AudioPlayerBlock from 'blocks/audio_player/audio_player'
import BioacousticsVisualizer from 'blocks/bioacoustics_visualizer/bioacoustics_visualizer'
import CardFlipGallery from 'blocks/card_flip_gallery/card_flip_gallery'
import Carousels from 'blocks/carousels/carousels'
import CarouselsV2 from 'blocks/carousels_v2/carousels'
import FeatureScroll from 'blocks/feature_scroll/feature_scroll'
import FeaturedStoryList from 'blocks/featured_story_list/featured_story_list'
import FullWidthFeature from 'blocks/full_width_feature/full_width_feature'
import LookbookSectionHeader from 'blocks/lookbook_section_header/lookbook_section_header'
import Theme from 'blocks/theme/theme'
import Gallery from 'blocks/gallery/gallery'
import MinecraftBlockBreaker from 'blocks/minecraft_block_breaker/minecraft_block_breaker'
import MinecraftBlockBreakerInstructions from 'blocks/minecraft_block_breaker_instructions/minecraft_block_breaker_instructions'
import MinecraftCarousel from 'blocks/minecraft_carousel/minecraft_carousel'
import MinecraftCraftingHeader from 'blocks/minecraft_crafting_header/minecraft_crafting_header'
import MinecraftFeature from 'blocks/minecraft_feature/minecraft_feature'
import MinecraftHeader from 'blocks/minecraft_header/minecraft_header'
import MinecraftHotspots from 'blocks/minecraft_hotspots/minecraft_hotspots'
import MinecraftKosovo from 'blocks/minecraft_kosovo/minecraft_kosovo'
import PixelTransition from 'blocks/pixel_transition/pixel_transition'
import SplitFeature from 'blocks/split_feature/split_feature'
import ReadingCoachCarousel from 'blocks/reading_coach_carousel/reading_coach_carousel'
import ReadingCoachDemo from 'blocks/reading_coach_demo/reading_coach_demo'
import FeelingsMonsterQuiz from 'blocks/feelings_monster_quiz/feelings_monster_quiz'
import FeelingsMonsterHoverGallery from 'blocks/feelings_monster_hover_gallery/feelings_monster_hover_gallery'
import FarmVibesDrone from 'blocks/farmvibes_drone/farmvibes_drone'
import KC7Quiz from 'blocks/kc7_quiz/kc7_quiz'
import Modal from 'blocks/modal/modal'
import VideoCarousel from 'blocks/video_carousel/video_carousel'

// Custom Pages
import PrideHeader from 'assets/js/pages/PrideHeader'

// Hero Templates
import Pride from 'hero-stories/pride-gaming/pride'
import NestedModal from 'hero-stories/pride-gaming/nested_modal/nested_modal'
import ControllerCarousel from 'hero-stories/pride-gaming/controller_carousel/controller_carousel'
import ControllerVideo from 'hero-stories/pride-gaming/controller_video/controller_video'

gsap.registerPlugin(ScrollTrigger)

/**
 * Add module objects alphabetically as needed
 * {
 *  selector: CSS selector
 *  Source: imported module
 * }
 */
const Modules = [
  ButtonToggle, // Needs to initialize early
  A11yWidget,
  AllStories,
  AdlamFullWidth,
  AdlamHomePage,
  AdlamPostcards,
  AudioPlayer,
  AudioPlayerBlock,
  BioacousticsVisualizer,
  Breadcrumb,
  ButtonVideo,
  CardCrosslink,
  CardFlipGallery,
  Carousels,
  CarouselsV2,
  ControllerCarousel,
  ControllerVideo,
  Card,
  CardInset,
  CardLegacy,
  Events,
  FeatureScroll,
  FeaturedStoryList,
  FeelingsMonsterQuiz,
  FeelingsMonsterHoverGallery,
  FocalPoint,
  FullWidthFeature,
  Gallery,
  GlobalTransitions,
  InvictusBgVideo,
  LookbookSectionHeader,
  MediaImage,
  MetaColor,
  MinecraftBlockBreaker,
  MinecraftBlockBreakerInstructions,
  MinecraftCarousel,
  MinecraftCraftingHeader,
  MinecraftFeature,
  MinecraftHeader,
  MinecraftHotspots,
  MinecraftKosovo,
  MobileVideo,
  NavHeight,
  NestedModal,
  ParallaxCards,
  ParallaxGrid,
  PixelTransition,
  PlayInView,
  Pride,
  PrideHeader,
  ScrollTarget,
  SplitFeature,
  SocialShare,
  ReadingCoachCarousel,
  ReadingCoachDemo,
  Theme,
  VideoPlayer,
  Xray,
  FarmVibesDrone,
  KC7Quiz,
  Modal,
  VideoCarousel
]

document.addEventListener('DOMContentLoaded', () => {
  setupImports()
  initLazyLoad()
  initRefreshScrollTrigger()
  initLowPowerMode()
  calcNavHeight()
  initIsMobileBreakpoint()
  initUHFSearch()

  ScrollTrigger.sort()
  ScrollTrigger.refresh()
})

function setupImports() {
  Modules.forEach(module => {
    Array.from(document.querySelectorAll(module.selector)).forEach(element => {
      new module.Source(element)
    })
  })
}

/**
 * @desc Initilizes Lazyloading. Parses page for data references
 * and triggers fade in.
 */
function initLazyLoad() {
  new LazyLoad()
}

function initRefreshScrollTrigger() {
  new RefreshScrollTrigger()
}

/**
 * @desc Initilizes LowPowerMode. Sets initial iphone low power mode setting
 * and listens for changes, broadcasting them as an event.
 */
function initLowPowerMode() {
  new LowPowerMode()
}

/**
 * @desc Calculates the height of the UHF and WordPress admin bars, if present,
 * and updates a CSS var with the value that can be used in calculations
 * throughout the site.
 */
function calcNavHeight() {
  new NavHeight()
}

/**
 * @desc Emits event on mobile breakpoint.
 */
function initIsMobileBreakpoint() {
  new IsMobileBreakpoint()
}

/**
 * @desc Emits event on mobile breakpoint.
 */
function initUHFSearch() {
  new UHFSearch()
}
